import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import type { FunctionComponent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useSignInMutation } from "Auth/Api/Mutations/useSignInMutation";
import { BlFormInput } from "Forms/Components/BlFormInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";

const SignInSchema = z.object({
  login: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  password: z
    .string()
    .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
});

type SignInFormModel = z.infer<typeof SignInSchema>;

type SignInFormProps = {};

export const SignInForm: FunctionComponent<SignInFormProps> = () => {
  const theme = useTheme();
  const form = useForm<SignInFormModel>({
    resolver: zodResolver(SignInSchema),
    defaultValues: { login: "", password: "" },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const { t } = useResource();
  const { mutate, error } = useSignInMutation();

  const submit = (data: SignInFormModel) => {
    mutate(data);
  };

  useHandleServerError({
    form,
    error,
    formKey: "login",
    resource: Resources.SignIn.Error,
  });

  return (
    <Box sx={{ width: "100%", maxWidth: "400px" }}>
      <Typography variant="h1" mb={Spacing.S}>
        {t(Resources.SignIn.Title)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={Spacing.L}>
        {t(Resources.SignIn.Subtitle)}
      </Typography>
      <form onSubmit={handleSubmit(submit)}>
        <Stack spacing={Spacing.M}>
          <Stack spacing={Spacing.M}>
            <BlFormInput
              control={control}
              name="login"
              autoComplete="login"
              errors={errors}
              label={t(Resources.SignIn.Username.Label)}
              placeholder={t(Resources.SignIn.Username.Label)}
            />
            <BlFormInput
              control={control}
              name="password"
              errors={errors}
              type="password"
              autoComplete="password"
              label={t(Resources.SignIn.Password.Label)}
              placeholder={t(Resources.SignIn.Password.Label)}
            />
            <UnstyledLink isUnderlined to={AppRouting.ResetPassword.path()}>
              <Typography variant="body2" lineHeight={1}>
                {t(Resources.SignIn.ForgottenPassword)}
              </Typography>
            </UnstyledLink>
          </Stack>
          <Stack spacing={Spacing.XS}>
            <BlButton type="submit" fullWidth color="primary">
              {t(Resources.SignIn.Button)}
            </BlButton>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography display="inline-block" variant="body2">
                {t(Resources.SignIn.DoNotHaveAccount)}
              </Typography>
              &nbsp;
              <UnstyledLink to={AppRouting.RegisterPartner.path()} isUnderlined>
                <Typography variant="body2">
                  {t(Resources.SignIn.RegisterNow)}
                </Typography>
              </UnstyledLink>
            </Box>
            <Box
              sx={{ height: 20 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <UnstyledLink
                to=""
                isUnderlined
                color={theme.palette.text.disabled}
              >
                <Typography variant="body2">
                  {t(Resources.SignIn.Cookies)}
                </Typography>
              </UnstyledLink>
              &nbsp;
              <Typography variant="body2" color={theme.palette.text.disabled}>
                |
              </Typography>
              &nbsp;
              <UnstyledLink
                color={theme.palette.text.disabled}
                to=""
                isUnderlined
              >
                <Typography variant="body2">
                  {t(Resources.SignIn.Help)}
                </Typography>
              </UnstyledLink>
            </Box>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};
